import React from "react";
import { ExternalLink, RefreshCw, AlertTriangle } from "lucide-react";
import DemandPageHeader from "../components/DemandPageHeader";
import { tickets as fetchTickets, health as snHealth } from "../api/serviceNowClient";
import { useDemandId } from "../context/DemandContext";

const cx = (...c) => c.filter(Boolean).join(" ");

function Pill({ children, tone = "gray" }) {
  const map = {
    gray: "bg-gray-100 text-gray-800",
    green: "bg-green-100 text-green-800",
    blue: "bg-blue-100 text-blue-800",
    amber: "bg-amber-100 text-amber-800",
    red: "bg-red-100 text-red-800",
  };
  return (
    <span className={cx("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium", map[tone])}>
      {children}
    </span>
  );
}

/** ServiceNow's own state wording varies by table; colour on meaning, not exact text. */
function stateTone(state) {
  const s = String(state || "").toLowerCase();
  if (!s) return "gray";
  if (s.includes("closed complete") || s.includes("approved")) return "green";
  if (s.includes("closed") || s.includes("cancel") || s.includes("reject")) return "red";
  if (s.includes("work in progress") || s.includes("progress")) return "blue";
  return "amber";
}

export default function ServiceNowTicketsPage() {
  const { setDemandId } = useDemandId();
  const [data, setData] = React.useState(null);
  const [health, setHealth] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await fetchTickets({ limit: 200 }));
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    snHealth().then(setHealth).catch(() => setHealth(null));
  }, []);

  const rows = data?.tickets || [];
  const orphans = data?.orphaned || [];

  return (
    <div>
      <DemandPageHeader
        title="ServiceNow Tickets"
        description="Every record AIEA has created in ServiceNow, with live status"
        breadcrumbPath="ServiceNow Tickets"
      >
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-60"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </DemandPageHeader>

      <div className="mx-auto max-w-7xl p-6 space-y-4">
        {health && (
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
            <Pill tone={health.reachable ? "green" : "red"}>
              {health.reachable ? "connected" : "unreachable"}
            </Pill>
            <span className="font-medium">{data?.instanceUrl || health.instance_url}</span>
            {data?.tables && (
              <span className="text-xs text-gray-500">
                tables: {data.tables.project} · {data.tables.demand}
              </span>
            )}
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900">
            Could not load tickets: {error}
          </div>
        )}

        {/* Stored links are still shown when the tenant is unreachable — the row is
            real even when its live status isn't available. */}
        {data?.warning && (
          <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <div>{data.warning}</div>
          </div>
        )}

        {loading && !data ? (
          <div className="p-10 text-center text-gray-500">Loading tickets…</div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center">
            <div className="text-gray-900 font-medium">No ServiceNow tickets yet</div>
            <div className="mt-1 text-sm text-gray-500">
              Take a demand through TAB approval, then use Execution Handoff to create one.
            </div>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div style={{ overflowX: "auto" }}>
              <table className="min-w-full text-sm" style={{ width: "100%" }}>
                <thead className="bg-gray-50">
                  <tr>
                    {["Ticket", "Demand", "Title", "Stage", "State", "Priority", "Updated", ""].map((h) => (
                      <th key={h} className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => (
                    <tr key={`${t.table}:${t.sysId}`} className="border-t hover:bg-gray-50">
                      <td className="px-3 py-2 whitespace-nowrap">
                        {/* record_url() returns "" when SN_INSTANCE_URL is unset, and an
                            <a href=""> reloads the current page — a dead link that looks live.
                            Show the number plainly instead, and say why on hover. */}
                        {t.url ? (
                          <a href={t.url} target="_blank" rel="noreferrer"
                             className="inline-flex items-center gap-1 font-medium text-indigo-600 hover:underline">
                            {t.number || "(no number)"} <ExternalLink size={12} />
                          </a>
                        ) : (
                          <span className="inline-flex items-center gap-1 font-medium text-gray-500"
                                title="No deep link: this environment has no ServiceNow instance URL configured.">
                            {t.number || "(no number)"}
                          </span>
                        )}
                        <div className="font-mono text-xs text-gray-400">{t.table}</div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {/* Selecting here sets the shared demand context, so the stage
                            screens open on this demand rather than resetting. */}
                        <button
                          onClick={() => setDemandId(t.demandId)}
                          className="font-mono text-xs text-indigo-600 hover:underline"
                          title="Make this the selected demand"
                        >
                          {t.demandId}
                        </button>
                      </td>
                      <td className="px-3 py-2">{t.demandTitle || <span className="text-gray-400">—</span>}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-600">{t.currentStage || "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {t.liveStateAvailable
                          ? <Pill tone={stateTone(t.state)}>{t.state || "—"}</Pill>
                          : <span className="text-xs text-gray-400">unavailable</span>}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-600">{t.priority || "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                        {t.updatedAt || (t.lastPushedAt ? `pushed ${String(t.lastPushedAt).slice(0, 16)}` : "—")}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {t.syncError && (
                          <span title={t.syncError}><Pill tone="red">sync error</Pill></span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Records in ServiceNow whose demand is gone locally. Surfaced rather than
            filtered out — an orphaned link is a real thing to notice. */}
        {orphans.length > 0 && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <div className="mb-2 text-sm font-medium text-amber-900">
              {orphans.length} ServiceNow record(s) with no matching demand in AIEA
            </div>
            <ul className="space-y-1 text-sm">
              {orphans.map((o) => (
                <li key={o.sys_id}>
                  <a className="text-indigo-600 hover:underline" href={o.url} target="_blank" rel="noreferrer">
                    {o.number}
                  </a>
                  <span className="text-amber-900"> — {o.shortDescription}</span>
                  <span className="ml-2 font-mono text-xs text-amber-700">{o.demandId}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
